import { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { colors, shadows, radius, space, lines } from '@ardoq/design-tokens';
import { FlexBox, FlexBoxProps } from '@ardoq/layout';
import { Header4, Paragraph } from '@ardoq/typography';
import { type I18nLocale, t } from '@ardoq/i18n';
import { ExpandButton } from './ExpandButton';

type HorizontalPosition = 'left' | 'right';

export type FloatingSidebarProps = {
  i18nLocale: I18nLocale;
  position: HorizontalPosition;
  title: string;
  subtitle: string;
  children: ReactNode;
  isExpanded?: boolean;
  onToggleExpand?: VoidFunction;
  height?: FlexBoxProps['height'];
  contentAriaLabel?: string;
  collapseLabel?: string;
  expandLabel?: string;
};

type PanelProps = {
  $position: HorizontalPosition;
  $isExpanded: boolean;
};

const horizontalPositionMap: Record<HorizontalPosition, string> = {
  left: `left: ${space.medium};`,
  right: `right: ${space.medium};`,
};

export const FloatingSidebarContainer = styled.div`
  position: relative;
`;

const defaultPanelWidth = css`
  width: 484px;
  /* Optimizing according to macbook 14" */
  @media (min-width: 1600px) {
    width: 545px;
  }
`;

const Panel = styled(FlexBox).attrs({
  flexDirection: 'column',
  gap: 'medium',
})<PanelProps>`
  ${({ $position }) => horizontalPositionMap[$position]}
  position: absolute;
  bottom: ${space.medium};
  background: ${colors.bgDefault};
  border-radius: ${radius.large};
  z-index: 1;
  transition: width 0.3s ease-in-out;
  ${({ $isExpanded }) => ($isExpanded ? 'width: 70%;' : defaultPanelWidth)}
  max-height: calc(100% - ${space.medium} - ${space.medium});
  box-shadow: ${shadows.shadowM};
  border: ${lines.solidGrey1};
`;

const ScrollableContent = styled.div`
  padding: 0 ${space.medium} ${space.medium} ${space.medium};
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
`;

export const FloatingSidebar = ({
  i18nLocale,
  position,
  title,
  subtitle,
  isExpanded = false,
  height,
  onToggleExpand,
  children,
  contentAriaLabel = t(
    i18nLocale,
    'integrations:floatingSidebar.contentAriaLabel',
    'Sidebar content'
  ),
  collapseLabel,
  expandLabel,
}: FloatingSidebarProps) => {
  return (
    <Panel
      $isExpanded={isExpanded}
      $position={position}
      role="complementary"
      aria-label={title}
      height={height}
    >
      {onToggleExpand && (
        <ExpandButton
          i18nLocale={i18nLocale}
          isExpanded={isExpanded}
          onToggleExpand={onToggleExpand}
          collapseLabel={collapseLabel}
          expandLabel={expandLabel}
        />
      )}
      <FlexBox
        gap="xxsmall"
        flexDirection="column"
        paddingX="medium"
        paddingTop="medium"
      >
        <Header4 color="textDefault">{title}</Header4>
        <Paragraph color="textSubtle" variant="text2">
          {subtitle}
        </Paragraph>
      </FlexBox>
      <ScrollableContent tabIndex={0} aria-label={contentAriaLabel}>
        {children}
      </ScrollableContent>
    </Panel>
  );
};
